import {alpha, SxProps, Theme} from '@mui/material';
import theme from 'src/theme';

/**
 * The shared detail-page card.
 *
 * The document had a 4px panel of its own and lived in eight of twelve columns; it is a portrait
 * page, so the width it was denied is exactly what it needed. Its own padding moves onto the parts
 * inside, so the header can sit flush against the card's top edge like every other section's.
 */
export const wrapperSx: SxProps<Theme> = {
    background: theme.palette.common.white,
    borderRadius: '14px',
    border: '1px solid rgba(0, 0, 0, 0.05)',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 4px 12px rgba(0,0,0,0.04)',
    overflow: 'hidden',
    '& .react-pdf__Document': {
        width: '100%',
        padding: theme.spacing(60 / 8),
        display: 'flex',
        justifyContent: 'center',
    },
    // No stretching here any more. The canvas is a raster: react-pdf draws it at the width it is
    // given and CSS blowing that up afterwards is what made the contract look pixelated. The page
    // is now rasterised at the size it is shown at — see PAGE_MAX_WIDTH in the component.
    '& .pdfPage .react-pdf__Page__canvas': {
        maxWidth: '100%',
        height: 'auto !important',
    },
};
export const titleSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.25),
    px: 3,
    py: 2,
};

export const sectionBadgeSx: SxProps<Theme> = {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    background: `linear-gradient(135deg, ${theme.palette.primaryColor[50]} 0%, ${theme.palette.primaryColor[100]} 100%)`,
    color: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    '& svg': {fontSize: '18px'},
};

export const sectionTitleSx: SxProps<Theme> = {
    fontSize: '1rem',
    fontWeight: 600,
    color: theme.palette.primary.main,
};

/** The page itself, on a ground, the way a document viewer shows one. */
export const pageStageSx: SxProps<Theme> = {
    background: theme.palette.neutral[50],
    borderTop: `1px solid ${theme.palette.neutral[100]}`,
    px: 3,
    py: 3,
};

export const pageCountSx: SxProps<Theme> = {
    fontSize: '0.75rem',
    color: theme.palette.neutral[500],
    fontVariantNumeric: 'tabular-nums',
};

/** The upload state — a drop zone and its button, inside the card rather than beside it. */
export const uploadSx: SxProps<Theme> = {
    px: 3,
    pb: 3,
    pt: 1,
};

export const documentWrapperSx: SxProps<Theme> = {
    position: 'relative',
};

export const SubmitBtnSx: SxProps = {
    width: '100%',
    alignItems: 'end',
};

/**
 * The document and the file it came from, side by side.
 *
 * The card used to run the width of the page while the sheet inside it is 820px, so most of it was
 * grey. The width now goes to what the document itself carries — its size, its file name, the way to
 * take a copy — which the contract card above does not repeat: that one is about the agreement, this
 * one is about the file.
 */
export const layoutSx: SxProps<Theme> = {
    display: 'grid',
    gridTemplateColumns: {xs: '1fr', lg: 'minmax(0, 940px) minmax(240px, 1fr)'},
    gap: 2,
    alignItems: 'start',
};

export const railHeadSx: SxProps<Theme> = {
    px: 2.5,
    py: 2,
    fontSize: '0.9375rem',
    fontWeight: 600,
    color: theme.palette.primary.main,
};

export const railRowSx: SxProps<Theme> = {
    px: 2.5,
    py: 1.75,
    borderTop: `1px solid ${theme.palette.neutral[100]}`,
    display: 'flex',
    flexDirection: 'column',
    gap: 0.6,
};

export const railLabelSx: SxProps<Theme> = {
    fontSize: '0.625rem',
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: theme.palette.neutral[500],
};

export const railValueSx: SxProps<Theme> = {
    fontSize: '0.875rem',
    fontWeight: 500,
    wordBreak: 'break-all',
};

/** Full width of the rail: it is the one action this column exists to offer. */
export const railActionSx: SxProps<Theme> = {
    px: 2.5,
    py: 2,
    borderTop: `1px solid ${theme.palette.neutral[100]}`,
    '& .MuiButton-root': {width: '100%'},
};

/** The page navigation, in the card's own footer rather than floating over the sheet. */
export const pageNavSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    py: 1.25,
    borderTop: `1px solid ${theme.palette.neutral[100]}`,
    background: theme.palette.common.white,
};

export const pageNavCountSx: SxProps<Theme> = {
    fontSize: '0.75rem',
    color: theme.palette.neutral[600],
    fontVariantNumeric: 'tabular-nums',
};

export const pageNavButtonSx: SxProps<Theme> = {
    width: '28px',
    height: '28px',
    borderRadius: '8px',
    border: `1px solid ${theme.palette.neutral[200]}`,
    color: theme.palette.primary.main,
    '&.Mui-disabled': {
        color: theme.palette.neutral[400],
        borderColor: alpha(theme.palette.neutral[300], 0.6),
    },
};
